// -------------------------------
// TDS SURCHARGE & CESS INTERFACES
// -------------------------------
export interface PendingTdsSurchargeCess {
    FinancialYearID?: number;
    TaxRegimeID?: number;
    IncomeThresholdForSurcharge1?: number;
    SurchargeRate1_Percentage?: number;
    IncomeThresholdForSurcharge2?: number;
    SurchargeRate2_Percentage?: number;
    HealthAndEducationCessRate_Percentage?: number;
    Status?: string; // active | inactive
}

export interface PendingDeleteTdsSurchargeCess {
    awaitingId?: boolean;
}

// -------------------------------
// CREATE TDS SURCHARGE & CESS — START
// -------------------------------
export const startCreateTdsSurchargeCessFlow = (
    setPending: (data: PendingTdsSurchargeCess | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({});

    pushAIChat("Sure! Please select the financial year.");
    speak("Please select the financial year.");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "financial-year-surcharge" }
        })
    );
};

// -------------------------------
// CREATE TDS SURCHARGE & CESS — PROCESS
// -------------------------------
export const processPendingTdsSurchargeCess = (
    text: string,
    pending: PendingTdsSurchargeCess,
    setPending: (data: PendingTdsSurchargeCess | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — FINANCIAL YEAR (via suggestion)
    if (!pending.FinancialYearID) {
        pushAIChat("Please select a financial year from the suggestions.");
        speak("Please select a financial year.");
        return;
    }

    // STEP 2 — TAX REGIME (via suggestion)
    if (!pending.TaxRegimeID) {

        return;
    }

    // STEP 3 — INCOME THRESHOLD 1
    if (pending.IncomeThresholdForSurcharge1 === undefined) {
        const val = Number(text.replace(/[^0-9]/g, ""));

        setPending({
            ...pending,
            IncomeThresholdForSurcharge1: val || 0
        });

        pushAIChat("Enter surcharge rate percentage.");
        speak("Enter surcharge rate percentage.");
        return;
    }

    // STEP 4 — SURCHARGE RATE 1
    if (pending.SurchargeRate1_Percentage === undefined) {
        const val = Number(text.replace(/[^0-9.]/g, ""));

        setPending({
            ...pending,
            SurchargeRate1_Percentage: val || 0
        });

        pushAIChat("Enter income threshold for surcharge 2.");
        speak("Enter income threshold for surcharge 2.");
        return;
    }

    // STEP 5 — INCOME THRESHOLD 2
    if (pending.IncomeThresholdForSurcharge2 === undefined) {
        const val = Number(text.replace(/[^0-9]/g, ""));

        setPending({
            ...pending,
            IncomeThresholdForSurcharge2: val || 0
        });

        pushAIChat("Enter surcharge rate 2 percentage.");
        speak("Enter surcharge rate 2 percentage.");
        return;
    }

    // STEP 6 — SURCHARGE RATE 2
    if (pending.SurchargeRate2_Percentage === undefined) {
        const val = Number(text.replace(/[^0-9.]/g, ""));

        setPending({
            ...pending,
            SurchargeRate2_Percentage: val || 0
        });

        pushAIChat("Enter Health & Education cess rate percentage.");
        speak("Enter health and education cess rate.");
        return;
    }

    // STEP 7 — HEALTH & EDUCATION CESS
    if (pending.HealthAndEducationCessRate_Percentage === undefined) {
        const val = Number(text.replace(/[^0-9.]/g, ""));

        if (isNaN(val)) {
            pushAIChat("Please provide a valid cess rate.");
            speak("Invalid cess rate.");
            return;
        }

        setPending({
            ...pending,
            HealthAndEducationCessRate_Percentage: val
        });

        pushAIChat("Should this be active or inactive?");
        speak("Should this be active or inactive?");
        return;
    }

    // STEP 8 — STATUS (FINAL)
    if (!pending.Status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat("Creating TDS Surcharge & Cess...");
        speak("Creating surcharge and cess.");

        window.dispatchEvent(
            new CustomEvent("ai-create-tds-surcharge-cess", {
                detail: {
                    ...pending,
                    Status: status
                }
            })
        );

        setPending(null);
    }
};

// -------------------------------
// DELETE TDS SURCHARGE & CESS — START
// -------------------------------
export const startDeleteTdsSurchargeCessFlow = (
    setPending: (data: PendingDeleteTdsSurchargeCess | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPending({ awaitingId: true });
    pushAIChat("Please provide the surcharge & cess record ID to delete.");
    speak("Please provide the surcharge and cess ID.");
};

// -------------------------------
// DELETE TDS SURCHARGE & CESS — PROCESS
// -------------------------------
export const processPendingDeleteTdsSurchargeCess = (
    text: string,
    pending: PendingDeleteTdsSurchargeCess,
    setPending: (data: PendingDeleteTdsSurchargeCess | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingId) return;

    const id = Number(text.replace(/[^0-9]/g, ""));

    if (!id) {
        pushAIChat("Please provide a valid ID.");
        speak("Invalid ID.");
        return;
    }

    pushAIChat("Deleting surcharge & cess...");
    speak("Deleting surcharge and cess.");

    window.dispatchEvent(
        new CustomEvent("ai-delete-tds-surcharge-cess", {
            detail: { id }
        })
    );

    setPending(null);
};
